import { type ReactNode, useSyncExternalStore } from 'react';
import { EventEmitter } from '@repo/lib/EventEmitter';
import { generateUUID } from '@repo/lib/crypto';

import { ToastGroup } from '../ToastGroup/ToastGroup.js';
import { ToastRecipeContent } from '../ToastRecipeContent/ToastRecipeContent.js';
import { type ToastPosition, type ToastProps } from '../Toast.js';

export type ToastItem = ToastProps & { id: string };

export type ToastRecipe = Omit<
  ToastProps,
  'children' | 'variant' | 'onClose'
> & {
  message: string;
};

export type ToastConfig = {
  maxToasts?: number;
  defaultPosition?: ToastPosition;
  defaultDuration?: number;
};

class ToastStore {
  private toasts: ToastItem[] = [];
  private config: ToastConfig = {};
  private readonly changeEmitter = new EventEmitter<void>();

  subscribe = (listener: () => void): (() => void) =>
    this.changeEmitter.on(listener);

  getSnapshot = (): ToastItem[] => this.toasts;

  setConfig = (config: ToastConfig): void => {
    this.config = config;
  };

  show = (props: Omit<ToastProps, 'onClose'>): string => {
    const id = generateUUID();

    const toastWithDefaults = {
      ...props,
      position: props.position ?? this.config.defaultPosition ?? 'bottomRight',
      duration: props.duration ?? this.config.defaultDuration ?? 3000,
    };

    const maxToasts = this.config.maxToasts ?? 3;

    if (maxToasts && this.toasts.length >= maxToasts) {
      this.toasts = [
        ...this.toasts.slice(this.toasts.length - maxToasts + 1),
        { ...toastWithDefaults, id },
      ];
    } else {
      this.toasts = [...this.toasts, { ...toastWithDefaults, id }];
    }

    this.changeEmitter.emit();
    return id;
  };

  hide = (id: string): void => {
    this.toasts = this.toasts.filter((item) => item.id !== id);
    this.changeEmitter.emit();
  };

  error = ({ message, ...options }: ToastRecipe): string =>
    this.show({
      children: <ToastRecipeContent message={message} icon="warning" />,
      variant: 'error',
      ...options,
    });

  info = ({ message, ...options }: ToastRecipe): string =>
    this.show({
      children: <ToastRecipeContent message={message} icon="info" />,
      variant: 'info',
      ...options,
    });

  success = ({ message, ...options }: ToastRecipe): string =>
    this.show({
      children: <ToastRecipeContent message={message} icon="check" />,
      variant: 'success',
      ...options,
    });
}

// This singleton is used to show toasts across the application
export const toast = new ToastStore();

const EMPTY_TOASTS: ToastItem[] = [];

export const ToastRoot = ({ children }: { children?: ReactNode }) => {
  const toasts = useSyncExternalStore(
    toast.subscribe,
    toast.getSnapshot,
    () => EMPTY_TOASTS,
  );

  return (
    <>
      {children}
      <ToastGroup toasts={toasts} hideToast={toast.hide} />
    </>
  );
};
